// ============================================================================
// ProjectWorkCard — the AI's project proposal, reviewed before it becomes a row.
// REBUILT 2026-09-03, addendum part 2C.
//
// ⛔ WHAT IT WAS. The tool asked the model for `service_type` and `category` —
// two columns that had already been removed from `projects` — and the card
// ignored both and made the AM pick the service by hand. It could not carry a
// sub, could not carry a classification, and wrote the project row with a
// hand-rolled insert that knew its own subset of columns.
//
// The result is in the audit table: FOUR proposals in the tool's entire life,
// the last on 29 July, against 39 for feedback and 30 for tasks. Two of the
// three it did commit are still sitting in Draft, one of them carrying $6,500.
// ⛔ A tool nobody uses is not a small problem — it is the whole problem.
//
// ⭐ WHAT IT IS NOW. The card shows what the AI worked out and lets the AM
// correct it: the service, the day, the sub, the money, and — when the work was
// NOT ordinary billable project work — what kind of thing it was. It commits
// through `createProjectWithLines`, the same path the entry screen uses, so
// there is one place that knows what a project is.
//
// ⚠️ TWO VOCABULARIES SHARE THE NAME `division_id`. `service_catalog.division_id`
// is CLEAN / FLOOR / GROUNDS and belongs to the SERVICE. `v_event_classes.
// division_id` is COVERAGE / RECOVERY / CONCESSION / CORRECTION / STARTUP and
// says what KIND of event this was. They are kept apart here by name —
// `division_id` and `event_division_id` — because one slot for both is how a
// service division ends up written as an event class.
// ============================================================================

import { useMemo, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import type { CopilotProposalBase } from '../../../shared/types';
import { CardShell } from './card-shell';
import { FacilityCombobox } from './facility-combobox';
import { ProjectServiceTypeSelect } from '../../project-work/service-type-select';
import { SmartSelect } from '../../../components/smart-select';
import { isProviderSelectable } from '../../project-work/lib/sub-picker';
import { useServiceCatalog } from '../../project-work/hooks/use-service-catalog';

interface FacilityOpt {
  facility_id: string;
  facility_name: string;
}

interface ProjectWorkCardProps {
  proposal: CopilotProposalBase;
  facilities: FacilityOpt[];
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

/** '' for null/undefined, so every comparison below is string-to-string. */
const str = (v: unknown): string => (v == null ? '' : String(v));
/** '' for a missing number — ⛔ NOT 0. A blank amount and a zero are different facts. */
const money = (v: unknown): string => (typeof v === 'number' ? String(v) : '');

export function ProjectWorkCard({
  proposal,
  facilities,
  isCommitting,
  onDoIt,
  onDiscard,
}: ProjectWorkCardProps) {
  const initial = proposal.input as {
    facility_id?: string;
    service_code?: string;
    division_id?: string;
    scope_text?: string;
    scope_qty?: number | null;
    service_date?: string | null;
    assigned_partner_id?: string | null;
    event_division_id?: string | null;
    reason_code?: string | null;
    bill_amount?: number | null;
    pay_amount?: number | null;
    notes?: string | null;
  };

  const catalog = useServiceCatalog();

  const [facility_id, setFacilityId] = useState(str(initial.facility_id));
  const [service_code, setServiceCode] = useState(str(initial.service_code));
  const [division_id, setDivisionId] = useState(str(initial.division_id));
  const [scope_text, setScopeText] = useState(str(initial.scope_text));
  const [service_date, setServiceDate] = useState(str(initial.service_date));
  const [assigned_partner_id, setPartner] = useState(str(initial.assigned_partner_id));
  const [bill_amount, setBill] = useState(money(initial.bill_amount));
  const [pay_amount, setPay] = useState(money(initial.pay_amount));
  const [notes, setNotes] = useState(str(initial.notes));

  // ⛔ THE CLASSIFICATION IS SHOWN, NOT EDITED HERE. Changing what a record IS
  // has its own screen with its own rules (2A, lib/reclassify.ts) — a second,
  // looser copy of that on a chat card is exactly the drift this rebuild is
  // fixing. The AM can correct it on the project the moment it is saved.
  const eventDivision = str(initial.event_division_id);
  const eventLabel = eventDivision
    ? (catalog.eventClasses.find((c) => c.division_id === eventDivision)?.display_name ?? eventDivision)
    : null;
  const reasonLabel = initial.reason_code
    ? (catalog.reasons.find((r) => r.reason_code === initial.reason_code)?.display_name ??
      str(initial.reason_code))
    : null;

  const wasEdited = useMemo(
    () =>
      facility_id !== str(initial.facility_id) ||
      service_code !== str(initial.service_code) ||
      division_id !== str(initial.division_id) ||
      scope_text !== str(initial.scope_text) ||
      service_date !== str(initial.service_date) ||
      assigned_partner_id !== str(initial.assigned_partner_id) ||
      bill_amount !== money(initial.bill_amount) ||
      pay_amount !== money(initial.pay_amount) ||
      notes !== str(initial.notes),
    [
      facility_id, service_code, division_id, scope_text, service_date,
      assigned_partner_id, bill_amount, pay_amount, notes, initial,
    ],
  );

  /**
   * ⛔ A BLANK AMOUNT IS NULL, NOT ZERO — TCSD-0062, and the advance gate reads
   * the difference: a NULL bill blocks the send, a zero one does not. Coercing
   * an empty box to 0 here would tell Finance the client is not being charged,
   * on a record where nobody said anything of the kind.
   */
  const num = (raw: string): number | null => {
    const t = raw.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const handleDoIt = () => {
    onDoIt(
      {
        facility_id,
        service_code: service_code.trim(),
        division_id: division_id || null,
        scope_text: scope_text.trim() || null,
        scope_qty: initial.scope_qty ?? null,
        service_date: service_date || null,
        assigned_partner_id: assigned_partner_id || null,
        // Passed through untouched: the classification is not editable here.
        event_division_id: eventDivision || null,
        reason_code: str(initial.reason_code) || null,
        bill_amount: num(bill_amount),
        pay_amount: num(pay_amount),
        notes: notes.trim() || null,
      },
      wasEdited,
    );
  };

  return (
    <CardShell
      title="Project work"
      icon={<CalendarClock className="h-4 w-4" />}
      accentClass="border-st-info-border-strong"
      status={proposal.status}
      committedLabel={proposal.target_row_id ? `Saved as ${proposal.target_row_id}` : 'Saved'}
      isCommitting={isCommitting}
      onDoIt={handleDoIt}
      onDiscard={onDiscard}
    >
      <Field label="Facility">
        <FacilityCombobox value={facility_id} options={facilities} onChange={setFacilityId} />
      </Field>

      <Field label="Division and service">
        <ProjectServiceTypeSelect
          divisionId={division_id}
          onDivisionChange={setDivisionId}
          value={service_code}
          onChange={setServiceCode}
          scopeText={scope_text}
          onScopeTextChange={setScopeText}
          selectClassName={inputClass}
          inputClassName={inputClass}
        />
      </Field>

      {/* ⭐ WHAT KIND OF THING THIS WAS. The old card had no slot for it at all,
          so every AI-drafted record was ordinary billable project work by
          construction — a covered shift filed as a job we bill for. */}
      {eventLabel && (
        <div className="rounded-md border border-st-warn-border-strong bg-st-warn-bg px-2.5 py-2 text-xs text-st-warn-fg-strong">
          Not ordinary project work — <span className="font-semibold">{eventLabel}</span>
          {reasonLabel ? `, ${reasonLabel}` : ''}.
          <span className="mt-0.5 block text-[11px] opacity-80">
            Change it on the project after saving if that is wrong.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Date of the work">
          <input
            type="date"
            value={service_date}
            onChange={(e) => setServiceDate(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Sub">
          <SmartSelect
            entity="service_provider"
            scope={facility_id ? { facility_id } : undefined}
            value={assigned_partner_id || null}
            onChange={(id) => setPartner(id || '')}
            isSelectable={isProviderSelectable}
            entityLabel="sub"
            placeholder="Search subs…"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Client rate (USD)">
          <input
            type="number"
            min="0"
            step="0.01"
            value={bill_amount}
            onChange={(e) => setBill(e.target.value)}
            placeholder="Leave blank if unknown"
            className={inputClass}
          />
        </Field>
        <Field label="Sub payment (USD)">
          <input
            type="number"
            min="0"
            step="0.01"
            value={pay_amount}
            onChange={(e) => setPay(e.target.value)}
            placeholder="Leave blank if unknown"
            className={inputClass}
          />
        </Field>
      </div>
      <p className="-mt-1 text-[11px] text-tcs-text-muted">
        Blank is not $0. Leave a box empty if nobody said an amount &mdash; a $0 you did not mean
        tells Finance the client is not being charged.
      </p>

      <Field label="Notes (coordination detail)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className={`${inputClass} resize-none`}
          placeholder="Who schedules it, lead time, shift preference, access constraints…"
        />
      </Field>
    </CardShell>
  );
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
